import type { JSX } from 'react';

import type { NaechsterSchritt, Schritthandlung, Schrittstand } from './naechsterSchritt';

/**
 * DIE SCHRITTLEISTE OBEN IM VORGANG (Auftrag CRM-196; SUP-17, SUP-18,
 * SUP-07, SUP-19, SUP-23).
 *
 * Wunsch von Ulf (11.10.2026): „… ebenso der Prozess, welcher schritt als
 * nächstes folgt."
 *
 * Eingang → Zuordnen → Antworten → Warten/Folgeaktion → Lösen, darunter der
 * Satz „Als Nächstes: …" mit einem Knopf, der dorthin führt. Erledigt,
 * aktuell und offen unterscheiden sich durch Zeichen und Schriftstärke, nicht
 * nur durch die Farbe. Auf dem Telefon (ÄA-63) fällt die Reihe weg; es bleibt
 * der Satz mit „Schritt n von m".
 */

const ZEICHEN: Readonly<Record<Schrittstand, string>> = {
  erledigt: '✓',
  aktuell: '●',
  offen: '○',
};

const VORLESEN: Readonly<Record<Schrittstand, string>> = {
  erledigt: 'erledigt',
  aktuell: 'aktuell',
  offen: 'offen',
};

export function Schrittleiste({
  schritt,
  handeln,
  laeuft,
  beiHandlung,
}: {
  readonly schritt: NaechsterSchritt;
  /** Ohne Recht oder auf dem Telefon nur der Satz, kein Knopf. */
  readonly handeln: boolean;
  readonly laeuft: boolean;
  readonly beiHandlung: (h: Schritthandlung) => void;
}): JSX.Element {
  const { schritte, nummer, satz, hinweis, handlung, erledigt } = schritt;

  return (
    <section className={`schrittleiste${erledigt ? ' schrittleiste--erledigt' : ''}`} aria-label="Nächster Schritt">
      <ol className="schrittleiste__schritte">
        {schritte.map((s) => (
          <li
            key={s.schluessel}
            className={`schrittleiste__schritt schrittleiste__schritt--${s.stand}`}
            aria-current={s.stand === 'aktuell' ? 'step' : undefined}
          >
            <span className="schrittleiste__marke">
              <span className="schrittleiste__zeichen" aria-hidden="true">
                {ZEICHEN[s.stand]}
              </span>
              <span>{s.name}</span>
              <span className="nur-vorlesen"> ({VORLESEN[s.stand]})</span>
            </span>
          </li>
        ))}
      </ol>
      <p className="schrittleiste__satz">
        {nummer !== null && (
          <span className="schrittleiste__nummer">
            Schritt {nummer} von {schritte.length} ·{' '}
          </span>
        )}
        <strong>{erledigt ? 'Erledigt:' : 'Als Nächstes:'}</strong> {satz}
        {handeln && handlung !== null && (
          <>
            {' '}
            <button
              type="button"
              className="knopf knopf--haupt knopf--klein"
              disabled={laeuft}
              onClick={() => beiHandlung(handlung)}
            >
              {handlung.beschriftung}
            </button>
          </>
        )}
      </p>
      {hinweis !== null && <p className="hilfe schrittleiste__hinweis">{hinweis}</p>}
    </section>
  );
}
